import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TickerSearchInput — Round 84f (Feb 14, 2026).
 *
 * STANDING RULE: every ticker-entry form anywhere in the Community
 * surface MUST use this component (not a raw <input> with manual
 * uppercase + slice). It calls `/api/stocks/search?q=…` with a 200ms
 * debounce and renders the same live dropdown as the Compare modal:
 * ticker · short name · dividend yield, click to lock in.
 *
 * Props
 * ─────
 *   value          : controlled string (for typed text — usually empty
 *                    after selection because the parent takes over).
 *   onChange(text) : optional, lets parents observe raw typing.
 *   onSelect(t)    : REQUIRED. Called with the picked ticker (UPPER).
 *   placeholder    : input placeholder.
 *   disabled       : disables input.
 *   blockedTickers : array of upper-case tickers shown disabled in the
 *                    dropdown with a hint string (e.g. "in another list").
 *   blockedHint    : hint shown next to a disabled ticker row.
 *   maxResults     : limit param passed to the API. Default 8.
 *   className      : extra classes on the wrapper.
 *   testid         : data-testid prefix for the input + dropdown.
 */
export default function TickerSearchInput({
  value,
  onChange: onChangeProp,
  onSelect,
  placeholder = 'Search ticker…',
  disabled = false,
  blockedTickers = [],
  blockedHint = '',
  maxResults = 8,
  className = '',
  testid = 'ticker-search',
  testId,   // backward-compat — old callers (Journal modal) pass `testId`
  // Aug 2026 (DVXV) — admin modals (Blocklist / Mark Liquidated) must
  // find NON-dividend rows too. Pass 'all' to search both sides of the
  // divide (admin-gated server-side; non-admins get coerced back to
  // dividend). Undefined = legacy behavior, dividend-only.
  dividendFilter,
}) {
  // Round 84f-hotfix — normalize callbacks at the top so the input's
  // onChange handler can call them unconditionally. Also handles the
  // legacy single-callback API used by JournalEntryModal where the
  // parent passes only `onChange` and expects it to fire on both
  // typing AND selection. When no `onSelect` is supplied, picking a
  // dropdown row calls `onChange(ticker)` instead.
  const onChange = typeof onChangeProp === 'function' ? onChangeProp : () => {};
  const tid = testId || testid;
  const [q, setQ] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  // Sync from parent (e.g. parent clears after select)
  useEffect(() => {
    setQ(value || '');
  }, [value]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setBusy(false);
      return undefined;
    }
    setBusy(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/stocks/search`, {
          params: {
            q: q.trim(),
            limit: maxResults,
            ...(dividendFilter ? { dividend_filter: dividendFilter } : {}),
          },
          ...(dividendFilter ? { withCredentials: true } : {}),
        });
        setResults(Array.isArray(r.data) ? r.data : []);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, maxResults]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const blockedSet = new Set((blockedTickers || []).map((t) => String(t).toUpperCase()));

  const handlePick = (ticker) => {
    if (blockedSet.has(ticker.toUpperCase())) return;
    setOpen(false);
    setResults([]);
    const picked = ticker.toUpperCase();
    // Two flows:
    //   1) Multi-pick (post composer, watchlist editor) — parent passes
    //      `onSelect` and wants the input CLEARED so they can pick the
    //      next ticker without manually deleting the previous text.
    //   2) Single-pick (Journal modal) — parent passes only `onChange`
    //      and wants the picked ticker LEFT in the input as the visible
    //      controlled value.
    if (typeof onSelect === 'function') {
      setQ('');
      onChange('');
      onSelect(picked);
    } else {
      setQ(picked);
      onChange(picked);
    }
  };

  return (
    <div
      ref={boxRef}
      className={`relative ${className}`}
      data-testid={tid}
    >
      <div className="flex items-center gap-2 border border-border rounded-md bg-background px-2.5 h-10 focus-within:ring-2 focus-within:ring-violet-500/40">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={q}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            setQ(next);
            setOpen(true);
            onChange(next);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              // Pick the first non-blocked result if dropdown is open.
              const first = (results || []).find((r) => !blockedSet.has(r.ticker.toUpperCase()));
              if (first) handlePick(first.ticker);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          className="flex-1 min-w-0 bg-transparent text-sm font-mono uppercase outline-none placeholder:text-muted-foreground placeholder:normal-case"
          maxLength={12}
          data-testid={`${tid}-input`}
        />
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : null}
      </div>

      {open && q.trim().length > 0 ? (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg"
          data-testid={`${tid}-results`}
        >
          {results.length === 0 && !busy ? (
            <div className="px-3 py-3 text-xs text-muted-foreground" data-testid={`${tid}-no-matches`}>
              No matches.
            </div>
          ) : null}
          {results.map((s) => {
            const isBlocked = blockedSet.has(String(s.ticker).toUpperCase());
            return (
              <button
                key={s.ticker}
                type="button"
                disabled={isBlocked}
                onClick={() => handlePick(s.ticker)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left border-b border-border/40 last:border-b-0 transition-colors ${
                  isBlocked
                    ? 'opacity-50 cursor-not-allowed bg-muted/40'
                    : 'hover:bg-muted cursor-pointer'
                }`}
                data-testid={`${tid}-result-${s.ticker}`}
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-bold font-mono">{s.ticker}</span>
                  <span className="truncate text-[11px] text-muted-foreground max-w-[160px] sm:max-w-[200px]">{s.name}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isBlocked && blockedHint ? (
                    <span className="text-[10px] italic text-muted-foreground">{blockedHint}</span>
                  ) : null}
                  <span className="text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400">
                    {s.dividend_yield ? `${s.dividend_yield.toFixed(2)}%` : '—'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
